import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Mail, Eye, Send, Flame, Swords, Sparkles } from "lucide-react";
import { toast } from "sonner";

function Row({ t }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 text-xs">
      <span className="text-dark font-medium truncate">{t.question}</span>
      <span className="shrink-0 tabular-nums"><span className="text-view-a font-bold">{t.pct_a}%</span> <span className="text-neutral">/</span> <span className="text-view-b font-bold">{100 - t.pct_a}%</span></span>
    </div>
  );
}

export default function DigestPanel() {
  const { api } = useAuth();
  const [digest, setDigest] = useState(null);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/digest/preview"); setDigest(data); } catch {}
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/digest/send");
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send digest");
    } finally {
      setSending(false);
    }
  };

  const runReminders = async () => {
    try {
      const { data } = await api.post("/admin/streak-reminders/run");
      toast.success(`Streak reminders sent to ${data.sent} user${data.sent === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reminders");
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-card p-5 mb-6" data-testid="digest-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-xl text-dark flex items-center gap-2">
            <Mail className="w-5 h-5 text-view-a" /> WEEKLY DIGEST
            <span className="text-[10px] font-body font-bold tracking-wider text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full" data-testid="digest-mock-badge">MOCK MODE</span>
          </h2>
          <p className="text-xs text-neutral mt-1">
            {digest ? `${digest.week_start} – ${digest.week_end} · ${digest.recipient_count} recipients` : "Loading…"}
            {digest?.last_sent && <> · Last sent {new Date(digest.last_sent.sent_at).toLocaleString()}</>}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={runReminders} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-orange-700 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors" data-testid="run-streak-reminders-btn">
            <Flame className="w-3.5 h-3.5" /> Send streak reminders now
          </button>
          <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-dark bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" data-testid="digest-preview-btn">
            <Eye className="w-3.5 h-3.5" /> {open ? "Hide" : "Preview"}
          </button>
          <button onClick={send} disabled={sending || !digest} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-view-a rounded-xl hover:bg-view-a-dark transition-colors disabled:opacity-50" data-testid="digest-send-btn">
            <Send className="w-3.5 h-3.5" /> {sending ? "Sending…" : "Send test digest"}
          </button>
        </div>
      </div>

      {open && digest && (
        <div className="mt-4 border border-gray-100 rounded-xl overflow-hidden" data-testid="digest-preview">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <p className="text-[10px] uppercase tracking-wider text-neutral font-bold">Subject</p>
            <p className="text-sm font-semibold text-dark" data-testid="digest-subject">{digest.subject}</p>
          </div>
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-3 gap-2 text-center">
              {[["Votes", digest.total_votes_week], ["Comments", digest.total_comments_week], ["New members", digest.new_members_week]].map(([l, v]) => (
                <div key={l} className="bg-gray-50 rounded-lg py-2">
                  <div className="font-heading text-xl text-dark">{v}</div>
                  <div className="text-[10px] text-neutral font-medium">{l} this week</div>
                </div>
              ))}
            </div>
            <div>
              <h4 className="flex items-center gap-1.5 text-xs font-bold text-dark uppercase tracking-wider mb-1"><Flame className="w-3.5 h-3.5 text-view-b" /> Hottest debates</h4>
              {digest.hottest.map((t) => <Row key={t.topic_id} t={t} />)}
            </div>
            <div>
              <h4 className="flex items-center gap-1.5 text-xs font-bold text-dark uppercase tracking-wider mb-1"><Swords className="w-3.5 h-3.5 text-purple-500" /> Most divided</h4>
              {digest.most_divided.map((t) => <Row key={t.topic_id} t={t} />)}
            </div>
            {digest.new_debates.length > 0 && (
              <div>
                <h4 className="flex items-center gap-1.5 text-xs font-bold text-dark uppercase tracking-wider mb-1"><Sparkles className="w-3.5 h-3.5 text-amber-500" /> New this week</h4>
                {digest.new_debates.map((t) => <Row key={t.topic_id} t={t} />)}
              </div>
            )}
            <p className="text-[11px] text-neutral italic">Emails are logged server-side only until an email provider key is configured.</p>
          </div>
        </div>
      )}
    </div>
  );
}
